import { useId } from 'react';
import { IconInbox } from './Icons.jsx';
import { t } from '../lib/i18n.js';

export function Field({ label, hint, error, required, className = '', children }) {
  const id = useId();
  const child =
    typeof children === 'function'
      ? children({ id, 'aria-describedby': hint ? `${id}-hint` : undefined })
      : children;
  return (
    <div className={`field ${required ? 'required' : ''} ${className}`}>
      {label && <label htmlFor={id}>{label}</label>}
      {typeof children === 'function' ? child : <div>{child}</div>}
      {hint && !error && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

/** Text input bound to a field label. */
export function Input({ label, hint, error, required, className, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, ...a11y }) => <input id={id} {...a11y} {...props} />}
    </Field>
  );
}

/**
 * Decimal input.
 *
 * Uses inputmode="decimal" rather than type="number": phone keyboards show the
 * right keys, and the value stays an exact string instead of passing through a
 * float on the way to the server.
 */
export function NumberInput({ label, hint, error, required, className, value, onChange, allowNegative = false, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, ...a11y }) => (
        <input
          id={id}
          {...a11y}
          type="text"
          inputMode={allowNegative ? 'text' : 'decimal'}
          autoComplete="off"
          value={value ?? ''}
          onChange={(e) => {
            const allowed = allowNegative ? /[^\d.-]/g : /[^\d.]/g;
            let next = e.target.value.replace(allowed, '').replace(/(\..*)\./g, '$1');
            if (allowNegative) {
              // A minus only makes sense leading the value - drop any others,
              // same treatment the decimal-point regex above already gives '.'.
              next = next.replace(/(?!^)-/g, '');
            }
            onChange(next);
          }}
          {...props}
        />
      )}
    </Field>
  );
}

export function Select({ label, hint, error, required, className, options, placeholder, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, ...a11y }) => (
        <select id={id} {...a11y} {...props}>
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => {
            const value = typeof opt === 'object' ? opt.value : opt;
            const text = typeof opt === 'object' ? opt.label : opt;
            return (
              <option key={value} value={value}>
                {text}
              </option>
            );
          })}
        </select>
      )}
    </Field>
  );
}

export function Textarea({ label, hint, error, className, ...props }) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {({ id, ...a11y }) => <textarea id={id} {...a11y} {...props} />}
    </Field>
  );
}

/** Two or three mutually exclusive choices, sized for a thumb. */
export function Segmented({ label, hint, value, onChange, options, className }) {
  return (
    <Field label={label} hint={hint} className={className}>
      <div className="segmented">
        {options.map((opt) => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          const text = typeof opt === 'object' ? opt.label : opt;
          return (
            <button
              key={optValue}
              type="button"
              aria-pressed={value === optValue}
              onClick={() => onChange(optValue)}
            >
              {text}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

export function Card({ title, actions, children, footer, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-header">
          {title && <h2>{title}</h2>}
          {actions}
        </header>
      )}
      {children && <div className="card-body">{children}</div>}
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

export function Stat({ label, value, hint, tone = '' }) {
  return (
    <div className={`stat ${tone}`}>
      <div className="label">{label}</div>
      <div className={`value ${String(value).length > 14 ? 'sm' : ''}`}>{value}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export const Badge = ({ tone = '', children }) => <span className={`badge ${tone}`}>{children}</span>;

export const PaymentBadge = ({ type }) => (
  <Badge tone={type === 'cash' ? 'cash' : 'debt'}>
    {type === 'cash' ? t.shared.cash : t.shared.debt}
  </Badge>
);

export function Empty({ title, message, action, icon }) {
  return (
    <div className="empty">
      {icon || <IconInbox />}
      <strong>{title}</strong>
      {message && <span className="small">{message}</span>}
      {action}
    </div>
  );
}

export const Loading = ({ label = t.app.loading }) => (
  <div className="loading-block">
    <span className="spinner" />
    <span className="small">{label}...</span>
  </div>
);

export function Alert({ tone = 'info', children }) {
  return (
    <div className={`alert alert-${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <span>{children}</span>
    </div>
  );
}

/** Shows an API failure without losing the offline case, which is not an error. */
export function ErrorState({ error, onRetry }) {
  if (!error) return null;
  return (
    <Alert tone={error.isNetworkError ? 'warn' : 'error'}>
      {error.isNetworkError ? t.connection.cachedNotice : error.message}
      {onRetry && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={onRetry}>
          {t.common.retry}
        </button>
      )}
    </Alert>
  );
}
